import { ApolloClient, ApolloProvider } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { storage as accountStorage } from '@bnnvara/accounts';
import getConfig from 'next/config';
import Head from 'next/head';
import fetch from 'node-fetch';
import PropTypes from 'prop-types';
import React from 'react';

import fragmentTypes from '../api/fragments/fragmentTypes.json';
import { getPossibleTypesFromFragmentTypes } from '../helpers/getPossibleTypesFromFragmentTypes';

const { publicRuntimeConfig } = getConfig();

let apolloClientInstance = null;

/**
 * Creates and configures the ApolloClient
 * @param  {Object} [initialState={}]
 */
const createApolloClient = (initialState = {}) => {
  const apolloCache = new InMemoryCache({
    possibleTypes: getPossibleTypesFromFragmentTypes(fragmentTypes),
  }).restore(initialState);

  const httpLink = new BatchHttpLink({
    fetch,
    uri: publicRuntimeConfig.apiEndpoint,
  });

  const authLink = setContext((_, { headers }) => {
    if (accountStorage.jwtToken) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${accountStorage.jwtToken}`,
        },
      };
    }
    return {
      headers,
    };
  });

  return new ApolloClient({
    cache: apolloCache,
    link: authLink.concat(httpLink),
    ssrMode: !process.browser,
  });
};

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {Object} initialState
 */
const initApolloClient = (initialState) => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!process.browser) {
    return createApolloClient(initialState);
  }

  // Reuse client on the client-side
  if (!apolloClientInstance) {
    apolloClientInstance = createApolloClient(initialState);
  }

  return apolloClientInstance;
};

/**
 * Creates and provides the apolloContext
 * to a next.js PageTree. Use it by wrapping
 * your PageComponent via HOC pattern.
 * @param {Function|Class} PageComponent
 * @param {Object} [config]
 * @param {Boolean} [config.ssr=true]
 */
const withApolloClient = (PageComponent, { ssr = true } = {}) => {
  const WithApollo = ({ apolloClient, apolloState, ...pageProps }) => {
    const client = apolloClient || initApolloClient(apolloState);

    return (
      <ApolloProvider client={client}>
        <PageComponent {...pageProps} />
      </ApolloProvider>
    );
  };

  WithApollo.defaultProps = {
    apolloState: null,
    apolloClient: null,
  };

  WithApollo.propTypes = {
    apolloState: PropTypes.shape({}),
    apolloClient: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }),
  };

  // Set the correct displayName in development
  if (process.env.NODE_ENV !== 'production') {
    const displayName =
      PageComponent.displayName || PageComponent.name || 'Component';

    if (displayName === 'App') {
      // eslint-disable-next-line
      console.warn('This withApollo HOC only works with PageComponents.');
    }

    WithApollo.displayName = `withApollo(${displayName})`;
  }

  if (ssr || PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx) => {
      const { AppTree } = ctx;

      // Initialize ApolloClient, add it to the ctx object so
      // we can use it in `PageComponent.getInitialProp`.
      // eslint-disable-next-line no-multi-assign
      const apolloClient = (ctx.apolloClient = initApolloClient());

      // Run wrapped getInitialProps methods
      let pageProps = {};
      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx);
      }

      // Only on the server:
      if (!process.browser) {
        // When redirecting, the response is finished.
        // No point in continuing to render
        if (ctx.res && ctx.res.finished) {
          return pageProps;
        }

        // Only if ssr is enabled
        if (ssr) {
          // Run all GraphQL queries
          const { getDataFromTree } = await import('@apollo/client/react/ssr');

          await getDataFromTree(
            <AppTree
              pageProps={{
                ...pageProps,
                apolloClient,
              }}
            />
          );

          // getDataFromTree does not call componentWillUnmount
          // head side effect therefore need to be cleared manually
          Head.rewind();
        }
      }

      // Extract query data from the Apollo store
      const apolloState = apolloClient.cache.extract();

      return {
        ...pageProps,
        apolloState,
      };
    };
  }

  return WithApollo;
};
export default withApolloClient;
